// Three jobs.
// 1. Stamp calls to OUR backend with X-Session-Id and X-Client-Page so the Live
//    Activity request log can attribute each request to a tab and a page.
// 2. When ANY request fails, print one plain-English console line: which service
//    failed, what the server said, and what that usually means.
// 3. Tell onFetchResponse() listeners about every response, so a module can react
//    to a call it did not make (the phone-number badges refresh after any SMS send).
// The wrapper never changes a response and never makes requests of its own; a
// failing request was issued by whatever app code sits further down the stack
// (the browser shows this file as the caller only because it wraps window.fetch).
import { getSessionId } from './sessionId';
import { getCurrentPage } from './presencePinger';

let installed = false;
const responseListeners = new Set();
const MAX_BODY_CHARS = 2000;
const PREFIX = '[NotesAI request failed]';
const TAIL = 'The request itself was made by the app code in this stack; the request monitor only reports it.';

// PostgREST / Postgres error codes → what they usually mean for this app.
const CODE_HINTS = {
  '42703': "the app asked for a column that does not exist in this schema's table",
  '42P01': 'the table does not exist in this schema',
  PGRST116: 'the app asked for exactly one row but the database returned none (or several); usually the record lives in a different schema or was deleted',
  PGRST205: 'the table is missing from Supabase, so a SQL migration has not been applied yet',
  PGRST301: 'the login token is missing or expired, so sign in again',
  '42501': 'the signed-in user is not allowed to read or change this (row-level security)',
  '23505': 'a record with the same unique value already exists',
  '22P02': 'a value was sent in the wrong format (for example text where the database expects a number or id)',
};

const STATUS_HINTS = {
  400: 'the server rejected the request as malformed',
  401: 'the user is not signed in or the login token expired',
  403: 'the signed-in user is not allowed to do this',
  404: 'that address does not exist on the server',
  406: 'the app asked for exactly one row but got none (or several)',
  409: 'the change conflicts with data that already exists',
  413: 'the request was too large',
  422: 'the server understood the request but the data in it was invalid',
  429: 'too many requests in a short time; the server is rate-limiting',
  500: 'the server hit an unexpected error; check the backend log for the traceback',
  502: 'the server is down or restarting',
  503: 'the server is down or restarting',
  504: 'the server took too long to answer',
};

function urlOf(input) {
  try {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;    // Request
    if (input && typeof input.href === 'string') return input.href;  // URL
  } catch (_) { /* ignore */ }
  return '';
}

function methodOf(input, init) {
  const m = (init && init.method) || (input && input.method) || 'GET';
  return String(m).toUpperCase();
}

export function isBackendUrl(input, backendUrl) {
  const url = urlOf(input);
  if (!url) return false;
  if (backendUrl && url.startsWith(backendUrl)) return true;
  if (url.startsWith('/api/') || url.startsWith('/corti/')) return true;
  try {
    const origin = window.location.origin;
    if (url.startsWith(`${origin}/api/`) || url.startsWith(`${origin}/corti/`)) return true;
  } catch (_) { /* ignore */ }
  return false;
}

/** Which service a URL belongs to, in words a person would use. */
export function serviceFor(url, backendUrl) {
  if (isBackendUrl(url, backendUrl)) return 'the NotesAI backend';
  let host = '';
  try { host = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').host; } catch (_) { /* ignore */ }
  if (/supabase\.co$/.test(host)) {
    if (url.includes('/auth/v1/')) return 'Supabase sign-in';
    if (url.includes('/storage/v1/')) return 'Supabase file storage';
    if (url.includes('/rest/v1/rpc/')) return 'a Supabase database function';
    if (url.includes('/rest/v1/')) return 'the Supabase database';
    return 'Supabase';
  }
  return host ? `an external service (${host})` : 'a service';
}

function shortPath(url) {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const path = decodeURIComponent(u.pathname + u.search);
    return path.length > 160 ? `${path.slice(0, 157)}...` : path;
  } catch (_) {
    return url;
  }
}

function tableFrom(url) {
  const m = /\/rest\/v1\/(?:rpc\/)?([^/?]+)/.exec(url);
  return m ? decodeURIComponent(m[1]) : null;
}

function parseBody(body) {
  if (!body) return {};
  try {
    const json = JSON.parse(body);
    if (json && typeof json === 'object') {
      return {
        code: json.code || json.error_code || null,
        message: json.message || json.error || json.detail || json.msg || null,
        hint: json.hint || null,
        details: json.details || null,
      };
    }
  } catch (_) { /* not JSON */ }
  const text = String(body).trim();
  return { message: text && !text.startsWith('<') ? text.slice(0, 300) : null };
}

/** One plain-English sentence for a failed HTTP response. Exported for tests. */
export function describeFailure({ url, method = 'GET', status, statusText = '', body = '', backendUrl = '' }) {
  const service = serviceFor(url, backendUrl);
  const table = service === 'the Supabase database' ? tableFrom(url) : null;
  const { code, message, hint, details } = parseBody(body);
  const where = table ? `table "${table}" (${shortPath(url)})` : shortPath(url);
  const said = message ? ` The server said: "${message}${details ? ` — ${details}` : ''}".` : '';
  const meaning = CODE_HINTS[code] || STATUS_HINTS[status] || null;
  const means = meaning ? ` What this usually means: ${meaning}.` : '';
  const serverHint = hint ? ` Supabase's own hint: ${hint}.` : '';
  return `${PREFIX} ${service} answered ${status} ${statusText || ''} for ${method} ${where}.${said}${means}${serverHint} ${TAIL}`.replace(/\s+/g, ' ').trim();
}

/** One plain-English sentence for a request that never got a response. Exported for tests. */
export function describeNetworkError({ url, method = 'GET', error, backendUrl = '' }) {
  const service = serviceFor(url, backendUrl);
  const reason = error && error.message ? error.message : String(error || 'unknown error');
  const likely = /supabase|NotesAI/.test(service)
    ? 'the server is down or restarting, the network dropped, or a browser extension blocked it'
    : 'the service is unreachable, the network dropped, or a browser extension blocked it';
  return `${PREFIX} Could not reach ${service} for ${method} ${shortPath(url)}: ${likely} (${reason}). ${TAIL}`;
}

export function withTelemetryHeaders(init, input, { getPage, getSession }) {
  const source = (init && init.headers) || (input && input.headers) || undefined;
  const headers = new Headers(source);
  if (!headers.has('X-Session-Id')) headers.set('X-Session-Id', getSession());
  if (!headers.has('X-Client-Page')) headers.set('X-Client-Page', getPage() || '/');
  return { ...(init || {}), headers };
}

// The user-events tracker already logs its own flush failures under [user_events_debug].
function skipReporting(url) {
  return url.includes('/api/user-events/batch/');
}

function reportFailedResponse(input, init, resp, backendUrl) {
  const url = urlOf(input);
  if (skipReporting(url)) return;
  const method = methodOf(input, init);
  const emit = (body) => {
    try {
      console.error(describeFailure({ url, method, status: resp.status, statusText: resp.statusText, body, backendUrl }));
    } catch (_) { /* never let reporting throw */ }
  };
  try {
    resp.clone().text().then((text) => emit(String(text || '').slice(0, MAX_BODY_CHARS)), () => emit(''));
  } catch (_) {
    emit('');
  }
}

function reportNetworkError(input, init, error, backendUrl) {
  const url = urlOf(input);
  if (skipReporting(url)) return;
  try {
    console.error(describeNetworkError({ url, method: methodOf(input, init), error, backendUrl }));
  } catch (_) { /* never let reporting throw */ }
}

/**
 * Call fn(url, method, response) after every fetch that got a response, once the
 * wrapper is installed. Returns an unsubscribe function. A throwing listener is
 * ignored and never affects the response.
 */
export function onFetchResponse(fn) {
  responseListeners.add(fn);
  return () => responseListeners.delete(fn);
}

function notifyListeners(input, init, resp) {
  if (!responseListeners.size) return;
  const url = urlOf(input);
  const method = methodOf(input, init);
  responseListeners.forEach((fn) => {
    try { fn(url, method, resp); } catch (_) { /* a listener never breaks a request */ }
  });
}

export function installFetchTelemetry({ backendUrl = '', getPage = getCurrentPage, getSession = getSessionId } = {}) {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return false;
  const original = window.fetch.bind(window);
  // Named so the frame reads "notesaiFetch" in stack traces instead of "(anonymous)".
  window.fetch = function notesaiFetch(input, init) {
    let call;
    try {
      call = isBackendUrl(input, backendUrl)
        ? original(input, withTelemetryHeaders(init, input, { getPage, getSession }))
        : original(input, init);
    } catch (_) {
      return original(input, init);
    }
    if (!call || typeof call.then !== 'function') return call;
    return call.then(
      (resp) => {
        if (resp && resp.ok === false) reportFailedResponse(input, init, resp, backendUrl);
        if (resp) notifyListeners(input, init, resp);
        return resp;
      },
      (error) => {
        reportNetworkError(input, init, error, backendUrl);
        throw error;
      }
    );
  };
  installed = true;
  return true;
}

export function _resetFetchTelemetryForTests() {
  installed = false;
}
